import { useState, useEffect } from "react";
import fleetApi, { fleetError } from "@/lib/fleetApi";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2 } from "lucide-react";
import { setDriverPaused } from "@/lib/pausedDrivers";

const field = "w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-600";
const EMPTY = { full_name: "", name: "", phone: "", email: "", license_number: "", license_expiration: "", status: "active", available: true, invite: true };

export default function DriverFormDialog({ driver, open, onOpenChange, onSaved }) {
  const editing = !!driver?.id;
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [code, setCode] = useState("");

  useEffect(() => {
    if (!open) return;
    setCode("");
    setForm(driver?.id ? {
      full_name: driver.full_name || "", name: driver.name || "", phone: driver.phone || "",
      license_number: driver.license_number || "", license_expiration: driver.license_expiration || "",
      status: driver.paused ? "inactive" : "active", available: !driver.paused,
    } : EMPTY);
  }, [open, driver]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (!form.full_name.trim()) { toast.error("Full name is required."); return; }
    if (!editing && form.invite && (!form.email.trim() || !form.license_number.trim() || !/^\d{4}-\d{2}-\d{2}$/.test(form.license_expiration))) {
      toast.error("To invite a driver, enter their email, license number and license expiry."); return;
    }
    setSaving(true);
    const payload = {
      ...form,
      name: form.name.trim() || form.full_name.trim().split(" ")[0] + " " + (form.full_name.trim().split(" ").slice(-1)[0]?.[0] || "") + ".",
    };
    try {
      if (editing) {
        const { status, available, ...rest } = payload;
        await fleetApi.put(`/drivers/${driver.id}`, rest);
        const pause = status === "inactive";
        if (pause !== !!driver.paused) await setDriverPaused(driver.id, pause);
      }
      else {
        const { invite, email, ...rest } = payload;
        const { data } = await fleetApi.post("/drivers", { ...rest, email: email.trim() || null, invite: !!invite });
        const c = data?.invite_code || data?.code || data?.invite?.code;
        onSaved?.();
        if (invite && c) { setCode(c); toast.success(`Invite emailed to ${email.trim()}.`); return; }
        toast.success(invite ? `Driver added and invite emailed to ${email.trim()}.` : "Driver added.");
        onOpenChange(false);
        return;
      }
      toast.success("Driver updated.");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      const code = e.response?.status;
      toast.error(code === 403 ? "You don't have permission to manage drivers."
        : fleetError(e, "Save failed."));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-slate-800 bg-[#0f172a] text-white" data-testid="driver-form-dialog">
        <DialogHeader><DialogTitle>{editing ? "Edit driver" : "Add driver"}</DialogTitle></DialogHeader>
        {code ? (
          <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-5 text-center" data-testid="driver-invite-success">
            <p className="text-sm text-emerald-200">Invite sent to {form.email}. They can also enter this code in the driver app:</p>
            <p className="mt-2 font-mono-data text-3xl font-bold tracking-[0.3em] text-white" data-testid="driver-invite-code">{code}</p>
          </div>
        ) : (
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-400">Full name *</label>
            <Input value={form.full_name} onChange={set("full_name")} className={field} data-testid="driver-full-name" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-400">Display name</label>
            <Input value={form.name} onChange={set("name")} placeholder="auto" className={field} data-testid="driver-display-name" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-400">Phone</label>
            <Input value={form.phone} onChange={set("phone")} className={field} data-testid="driver-phone" />
          </div>
          {!editing && (
            <div className="col-span-2">
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-400">Email{form.invite ? " *" : ""}</label>
              <Input type="email" value={form.email} onChange={set("email")} className={field} data-testid="driver-email" />
            </div>
          )}
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-400">License #{!editing && form.invite ? " *" : ""}</label>
            <Input value={form.license_number} onChange={set("license_number")} className={field} data-testid="driver-license" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-400">License expiration{!editing && form.invite ? " *" : ""}</label>
            <Input type="date" value={form.license_expiration} onChange={set("license_expiration")} className={field} data-testid="driver-license-exp" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-400">Status</label>
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value, available: e.target.value === "active" })} className={field} data-testid="driver-status">
              <option value="active">active</option><option value="inactive">inactive</option>
            </select>
          </div>
          <div className="flex items-end gap-2 pb-2">
            <Checkbox id="drv-avail" checked={form.available} onCheckedChange={(v) => setForm({ ...form, available: v === true, ...(editing ? { status: v === true ? "active" : "inactive" } : {}) })} data-testid="driver-available" />
            <label htmlFor="drv-avail" className="text-sm text-slate-300">Available for dispatch</label>
          </div>
          {!editing && (
            <div className="col-span-2 flex items-center gap-2 rounded-md border border-slate-800 bg-slate-900/60 px-3 py-2">
              <Checkbox id="drv-invite" checked={form.invite} onCheckedChange={(v) => setForm({ ...form, invite: v === true })} data-testid="driver-invite" />
              <label htmlFor="drv-invite" className="text-sm text-slate-300">Invite to the driver app <span className="text-xs text-slate-500">— emails them a sign-up code</span></label>
            </div>
          )}
        </div>
        )}
        <DialogFooter>
          {code ? (
            <Button onClick={() => onOpenChange(false)} className="bg-blue-700 text-white hover:bg-blue-800" data-testid="driver-invite-done">Done</Button>
          ) : (<>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-300 hover:bg-slate-800 hover:text-white" data-testid="driver-form-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="gap-2 bg-blue-700 text-white hover:bg-blue-800" data-testid="driver-form-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {editing ? "Save" : form.invite ? "Add & invite" : "Add driver"}
          </Button>
          </>)}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
